import { z } from "zod";
import { WorkItemSchema } from "../workflows/types";

// Mirrors NextExplain on the wire (Galileo.Einstein.Next.Contracts.Ranking).
// Returned alongside a *fresh* serve only — a re-serve runs no ranking, so
// the dispenser returns a null explain there. The trace is purely
// informational: it lets the served-item surface answer the politically
// contested "why this item?" question without a second call. Fields are
// required because the server always populates the whole record on a serve.
export const NextExplainSchema = z.object({
    // 0 = the dynamic-scored floor; >= 1 = an absolute priority tier that
    // out-ranks any scored queue structurally.
    tier: z.number().int(),
    // "absolute" | "dynamic" — the strategy that placed the served queue in
    // its tier. Left as a free string (rather than an enum) so a future
    // strategy token renders rather than failing the parse at the boundary.
    tierStrategy: z.string(),
    score: z.number(),
    taskTypeWeight: z.number(),
    queuePressure: z.number(),
    overdueSeconds: z.number(),
    // "oldest-due" | "most-recent" — the tiebreak key applied within the tier.
    tiebreak: z.string(),
});

export type NextExplain = z.infer<typeof NextExplainSchema>;

// Mirrors NextWorkItemResponse. Three outcomes, all HTTP 200:
//   - Fresh serve: item populated, reServed false, explain populated.
//   - Re-serve   : item populated (the caller's existing lock), reServed
//                  true, explain null (no ranking ran).
//   - Empty      : item null, reServed false, explain null ("nothing to do").
// The discriminator the UI cares about is `item === null` (nothing to do)
// vs not; `reServed` only tunes the toast copy.
export const NextWorkItemResponseSchema = z.object({
    item: WorkItemSchema.nullable().optional().default(null),
    reServed: z.boolean().optional().default(false),
    explain: NextExplainSchema.nullable().optional().default(null),
});

export type NextWorkItemResponse = z.infer<typeof NextWorkItemResponseSchema>;
